import { useEffect, useMemo } from 'react'
import { Modal, Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { PrimaryButton } from '../../../components/Button'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import pxToRem from '../../../helpers/pxToRem'
import { tabNames } from '../../../routers/helpers'
import { DashboardTabNames } from '../../../routers/type'
import {
    selectDerivedBasicDetailsState,
    selectDerivedEmergencyContactState,
    selectDerivedIdentityState,
    selectDerivedProfilePhotoState,
    selectTeacherAccreditationChecks,
    selectTeacherAccreditationLive,
    selectTeacherAccountState,
    selectTeacherEmploymentSettings,
    selectTeacherReference,
    selectTeacherWorkingRights,
    selectTeacherSetLocation,
    selectTeacherServices,
    selectTeacherWorkingHistory,
    accreditationDocDefault,
} from '../../../redux/teacherSlice'
import {
    setFromEdit,
    setOpenRegModal,
    setProfilePhotoStep,
    setStateList,
    setStep,
} from '../../../redux/userRegistrationSlice'
import { RootState } from '../../../redux/store'
import { REG_STEP } from '../../../redux/types'
import { modalStyle } from './index'
import Names from './components/Names'
import Contact from './components/Contact'
import ProfilePhoto from './components/ProfilePhoto'
import { WorkingRightsWrapper } from './components/WorkingRights'
import { PrimaryIdentityWrapper } from './components/PrimaryIdentity'
import { SecondaryIdentityWrapper } from './components/SecondaryIdentity'
import { PhotoHoldingIDWrapper } from './components/PhotoHoldingID'
import Reference from './components/Reference'
import EmergencyContact from './components/EmergencyContact'
import AccreditationLevel from './components/AccreditationLevel'
import {
    createKey,
    useAccreditationConfigs,
} from '../../../hooks/useAccreditationConfig'
import { EmploymentSetting } from '../../../firebase/types-teacher'
import { FieldState_v2 } from '../../../schemas/teacherReg'
import { TeachingDocumentationWrapper } from './components/TeachingDocumentation_v2'
import WorkingHistoryComponent from './components/WorkingHistory'

interface IRegistrationModalsV2 {
    buttonText?: string
    withButton?: boolean
}

type ModalStepV2 = {
    id: string
    editId: string
    state: FieldState_v2
    modal: JSX.Element
    setting?: EmploymentSetting
}

const RegistrationModals_v2 = ({
    buttonText,
    withButton,
}: IRegistrationModalsV2) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const open = useSelector((state: RootState) => state.userReg.openRegModal)
    const step = useSelector((state: RootState) => state.userReg.step)
    const fromEdit = useSelector((state: RootState) => state.userReg.fromEdit)
    const profilePhotoStep = useSelector(
        (state: RootState) => state.userReg.profilePhotoStep
    )
    const activeEmploymentSetting = useSelector(
        (state: RootState) => state.userReg.employmentSetting
    )

    const setLocation = useSelector(selectTeacherSetLocation)
    const basicDetails = useSelector(selectDerivedBasicDetailsState)
    const profileDetails = useSelector(selectDerivedProfilePhotoState)
    const identityDetails = useSelector(selectDerivedIdentityState)
    const emergencyDetails = useSelector(selectDerivedEmergencyContactState)
    const workingRights = useSelector(selectTeacherWorkingRights)
    const workingHistory = useSelector(selectTeacherWorkingHistory)
    const reference = useSelector(selectTeacherReference)
    const accountState = useSelector(selectTeacherAccountState)
    const employmentSettings = useSelector(selectTeacherEmploymentSettings)
    const services = useSelector(selectTeacherServices)
    const accreditationChecks = useSelector(selectTeacherAccreditationChecks)
    const accreditationLive = useSelector(selectTeacherAccreditationLive)

    const settings: EmploymentSetting[] = useMemo(() => {
        if (employmentSettings.status !== 'success') return []

        return Object.values(employmentSettings.data)
    }, [employmentSettings])

    const accreditationConfigs = useAccreditationConfigs(settings)

    const globalLoading =
        basicDetails.loading ||
        profileDetails.loading ||
        identityDetails.loading ||
        emergencyDetails.loading ||
        workingRights.status !== 'success' ||
        reference.status !== 'success' ||
        accountState.status !== 'success' ||
        employmentSettings.status !== 'success' ||
        accreditationLive.status !== 'success' ||
        workingHistory.status !== 'success' ||
        accreditationConfigs.pending

    const modalList = useMemo<ModalStepV2[]>(() => {
        if (globalLoading || employmentSettings.status !== 'success') {
            return []
        }

        const accredLevelState: FieldState_v2 =
            services.status === 'success' &&
            Object.keys(services.data).length > 0
                ? 'submitted'
                : 'incomplete'

        const baseSteps: ModalStepV2[] = [
            {
                id: REG_STEP.NAMES,
                editId: REG_STEP.NAMES,
                modal: <Names />,
                state: basicDetails.itemStates.names,
            },
            {
                id: REG_STEP.CONTACT,
                editId: REG_STEP.CONTACT,
                modal: <Contact />,
                state: basicDetails.itemStates.contact,
            },
            {
                id: REG_STEP.PROFILE_PHOTO,
                editId: REG_STEP.PROFILE_PHOTO,
                modal: <ProfilePhoto />,
                state: profileDetails.state,
            },
            {
                id: REG_STEP.WORKING_RIGHTS,
                editId: REG_STEP.WORKING_RIGHTS,
                modal: <WorkingRightsWrapper />,
                state: workingRights.data.state,
            },
            {
                id: REG_STEP.PRIMARY_DOC,
                editId: REG_STEP.PRIMARY_DOC,
                modal: <PrimaryIdentityWrapper />,
                state: identityDetails.itemStates.primary,
            },
            {
                id: REG_STEP.SECONDARY_DOC,
                editId: REG_STEP.SECONDARY_DOC,
                modal: <SecondaryIdentityWrapper />,
                state: identityDetails.itemStates.secondary,
            },
            {
                id: REG_STEP.HOLDING_ID,
                editId: REG_STEP.HOLDING_ID,
                modal: <PhotoHoldingIDWrapper />,
                state: identityDetails.itemStates.photoHoldingId,
            },
        ]

        const accreditationSteps: ModalStepV2[] =
            accreditationConfigs.data.flatMap((el) => {
                const { setting, data } = el

                const settingKey = createKey(setting)
                const steps: ModalStepV2[] = Object.entries(data || {}).map(
                    ([type, config]) => {
                        const liveDoc =
                            Object.values(accreditationLive.data || {}).find(
                                (doc) => doc.accreditationType === type
                            ) || accreditationDocDefault
                        const state: FieldState_v2 =
                            liveDoc.state || 'incomplete'

                        return {
                            id: `${settingKey}:${type}`,
                            editId: type || '',
                            state,
                            setting,
                            modal: (
                                <TeachingDocumentationWrapper
                                    key={`${settingKey}:${type}`}
                                    details={config}
                                    employmentSetting={setting.type}
                                />
                            ),
                        }
                    }
                )

                if (
                    setting.type === 'school' &&
                    setting.locationState === 'NSW' &&
                    !['approved', 'onHold'].includes(accountState.data)
                ) {
                    steps.push({
                        id: `${createKey(setting)}:${REG_STEP.ACCREDITATION_LEVEL}`,
                        editId: REG_STEP.ACCREDITATION_LEVEL,
                        state: accredLevelState,
                        setting,
                        modal: <AccreditationLevel />,
                    })
                }

                return steps
            })

        return [
            ...baseSteps,
            ...accreditationSteps,
            {
                id: REG_STEP.WORKING_HISTORY,
                editId: REG_STEP.WORKING_HISTORY,
                modal: <WorkingHistoryComponent />,
                state: workingHistory.data.state,
            },
            {
                id: REG_STEP.REFERENCE,
                editId: REG_STEP.REFERENCE,
                modal: <Reference />,
                state: reference.data.state,
            },
            {
                id: REG_STEP.EMERGENCY_CONTACTS,
                editId: REG_STEP.EMERGENCY_CONTACTS,
                modal: <EmergencyContact />,
                state: emergencyDetails.state,
            },
        ]
    }, [
        basicDetails,
        accountState.data,
        services.data,
        services.status,
        accreditationLive.data,
        emergencyDetails,
        employmentSettings,
        globalLoading,
        identityDetails,
        profileDetails,
        reference,
        workingRights,
        workingHistory,
        accreditationConfigs,
    ])

    const currentStep = modalList[step]

    const resolveEditIndex = useMemo(() => {
        if (modalList.length === 0 || !fromEdit) return -1

        const matches = modalList
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.editId === fromEdit)

        if (matches.length === 0) return -1
        if (matches.length === 1) return matches[0].index

        const exactActiveSettingMatch = matches.find(({ item }) => {
            if (!item.setting || !activeEmploymentSetting) return false
            return (
                item.setting.locationState ===
                    activeEmploymentSetting.locationState &&
                item.setting.type === activeEmploymentSetting.type
            )
        })
        if (exactActiveSettingMatch) return exactActiveSettingMatch.index

        const locationMatch = matches.find(
            ({ item }) =>
                item.setting?.locationState === setLocation.data?.homeState
        )
        if (locationMatch) return locationMatch.index

        const homeStateMatch = matches.find(
            ({ item }) =>
                item.setting?.locationState === setLocation.data?.homeState
        )
        if (homeStateMatch) return homeStateMatch.index

        return matches[0].index
    }, [
        activeEmploymentSetting,
        fromEdit,
        modalList,
        setLocation.data?.homeState,
    ])

    useEffect(() => {
        dispatch(setStateList(modalList.map((el) => el.state)))
    }, [dispatch, modalList])

    useEffect(() => {
        if (globalLoading) return

        if (step >= 0 && step < modalList.length) {
            return
        }

        if (modalList.length > 0) {
            dispatch(setOpenRegModal(false))
            dispatch(setStep(0))
            navigate(
                `${tabNames.app.teacher.index}/${DashboardTabNames.REVIEW}`
            )
        }
    }, [dispatch, globalLoading, modalList.length, navigate, step])

    useEffect(() => {
        if (globalLoading || !open) {
            if (!open) {
                dispatch(setStep(0))
                dispatch(setFromEdit(undefined))
            }
            return
        }

        if (fromEdit !== undefined) {
            if (resolveEditIndex >= 0) {
                dispatch(setStep(resolveEditIndex))
            }
            return
        }

        if (profilePhotoStep !== undefined) {
            const index = modalList.findIndex(
                (el) => el.editId === REG_STEP.PROFILE_PHOTO
            )
            if (index > -1) {
                dispatch(setStep(index))
            }
            dispatch(setProfilePhotoStep(undefined))
            return
        }

        // if (
        //     reg.accountState?.firstTime === undefined ||
        //     reg.accountState?.firstTime === true
        // ) {
        //     dispatch(setStep(0))
        // }
    }, [
        dispatch,
        fromEdit,
        globalLoading,
        modalList,
        open,
        profilePhotoStep,
        // accountState?.firstTime,
        resolveEditIndex,
    ])

    // logger.debug('RegistrationModals_v2 rendered')

    return (
        <>
            {withButton && (
                <PrimaryButton
                    sx={{
                        width: '80%',
                        fontSize: pxToRem(16),
                        minHeight: pxToRem(50),
                    }}
                    onClick={() => {
                        navigate(
                            `${tabNames.app.teacher.index}/${DashboardTabNames.MY_ACCOUNT}`
                        )
                    }}
                >
                    {buttonText || 'Click here'}
                </PrimaryButton>
            )}
            <Modal open={open}>
                <Stack sx={{ ...modalStyle, height: '95%', width: '95%' }}>
                    {globalLoading || !currentStep ? (
                        <LoadingSpinner />
                    ) : (
                        currentStep.modal
                    )}
                </Stack>
            </Modal>
        </>
    )
}

export default RegistrationModals_v2
